// TabHub — Phase 12 shared tabbed-hub container.
//
// A hub page is a thin wrapper that picks the active tab from the URL
// (or local state) and renders the corresponding panel. The tab strip
// stays sticky at the top of the hub content area.
//
// Two usage modes:
//
//   1. Route-driven (preferred for hubs like /me, /people, /finance):
//      The hub mounts as the parent of several routes; each route's
//      <TabHub.Item to="..." /> participates in the visible tab strip.
//      The active tab is determined by react-router NavLink matching.
//      Children of the parent <Route> render via <Outlet/>.
//
//   2. State-driven (for cases where you don't want URLs per tab):
//      Use <TabHub.Strip activeKey={..} onChange={..} /> and render
//      the panel content yourself.
//
// Phase 1 UI overhaul (2026-05-15): visual tokens realigned with the
// MagentaTabBar pattern used by the agent SPA's hubs (PlayersHub,
// AgentsHub, ProfileHub, RewardsHub). Specifically:
//   • Header title icon: arena/neon ring (was fuchsia-only).
//   • Title/subtitle text: arena-900/neon-200 (was neutral-900/white-55).
//   • TabStrip pills: bg-white/70 dark:bg-arena-800/60 with arena
//     border (was neutral-200/white-10).
//   • SegmentedTabs container: same arena-tinted glass strip.
//   • Active pill uses VIOLET (admin's identity), matching TopPillNav.
//   • Hardcoded fuchsia/neutral references gone — every surface is
//     now arena/neon tokens so the hub bar matches the player/agent
//     visual language pixel-for-pixel.
//
// Mobile: horizontally scrollable strip. Padding unchanged.

import { ReactNode } from 'react';
import { NavLink, useLocation, Outlet } from 'react-router-dom';
import { motion } from 'motion/react';
import Icon from '../../components/Icon';

export type TabDef = {
  /** URL the tab navigates to (route-driven mode). */
  to?: string;
  /** Stable key (state-driven mode). */
  key?: string;
  label: string;
  /** Material icon name. Optional. */
  icon?: string;
  /** Match URLs starting with this prefix as active. Defaults to exact `to`. */
  matchPrefix?: string;
  /** Optional small badge count (e.g., pending queue size). */
  count?: number;
  /** Hide tab if false. Useful for permission gating. */
  show?: boolean;
};

interface TabHubProps {
  title: string;
  titleIcon?: string;
  subtitle?: string;
  tabs: TabDef[];
  /** Panel content when state-driven. Omit for route-driven mode (uses <Outlet/>). */
  children?: ReactNode;
  /** Called when a state-driven tab is clicked. Pass the tab.key. Omit for route-driven. */
  onTabChange?: (key: string) => void;
  /** Active tab key (state-driven). */
  activeKey?: string;
  /** Right-side action area in the header (e.g., a "Refresh" button). */
  headerRight?: ReactNode;
  /**
   * Phase 16.9: tab visual style.
   *   'pill'      = bright violet rounded-full pills (matches TopPillNav).
   *   'segmented' = quiet flat container with subtle white-on-white
   *                 active state (matches the entity-detail pages).
   * Defaults to 'segmented' since that's the platform-wide preference
   * after the 16.8 detail-page refactor. Pass variant="pill" on any
   * specific hub if you want the bright look back.
   */
  variant?: 'pill' | 'segmented';
  /**
   * Phase 2 chrome swap: agent hubs (People / Finance / MePool)
   * pass 'xl' to narrow the column from the platform-wide default
   * (max-w-screen-2xl). Other consumers (admin hubs) keep the
   * default unless they opt in. Accepts the Tailwind sizes that
   * make sense for a hub: 'xl', '2xl', '3xl', '4xl', 'screen-2xl'.
   */
  maxWidth?: 'xl' | '2xl' | '3xl' | '4xl' | 'screen-2xl';
}

const MAX_W_CLASS: Record<NonNullable<TabHubProps['maxWidth']>, string> = {
  'xl':         'max-w-xl',
  '2xl':        'max-w-2xl',
  '3xl':        'max-w-3xl',
  '4xl':        'max-w-4xl',
  'screen-2xl': 'max-w-screen-2xl',
};

export default function TabHub({
  title, titleIcon, subtitle, tabs, children, onTabChange, activeKey, headerRight,
  variant = 'segmented',
  maxWidth = 'screen-2xl',
}: TabHubProps) {
  const visibleTabs = tabs.filter((t) => t.show !== false);

  return (
    <div className={`${MAX_W_CLASS[maxWidth]} mx-auto px-2 sm:px-3 lg:px-6 py-3 lg:py-4 space-y-4`}>
      {/* Hub header — title block uses arena/neon tokens so the look
          matches the agent hubs (PlayersHub/AgentsHub) verbatim. The
          title icon ring is the admin's violet accent for visual
          continuity with TopPillNav's active pill. */}
      <motion.header
        initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
        className="px-2 lg:px-4"
      >
        <div className="flex items-center gap-3">
          {titleIcon && (
            <span className="grid place-items-center w-10 h-10 rounded-xl
                             bg-violet-50 text-violet-700 ring-1 ring-violet-100
                             dark:bg-violet-400/15 dark:text-violet-300 dark:ring-violet-400/30">
              <Icon name={titleIcon} size={22} filled />
            </span>
          )}
          <div className="min-w-0 flex-1">
            <h1 className="text-xl sm:text-2xl font-display font-semibold text-arena-900 dark:text-white tracking-tight">
              {title}
            </h1>
            {subtitle && (
              <p className="text-[12.5px] text-arena-600 dark:text-neon-200/70 truncate">{subtitle}</p>
            )}
          </div>
          {headerRight && <div className="shrink-0">{headerRight}</div>}
        </div>
      </motion.header>

      {/* Tab strip — variant picks the visual style. */}
      <div className="px-2 lg:px-4">
        {variant === 'segmented'
          ? <SegmentedTabs tabs={visibleTabs} onTabChange={onTabChange} activeKey={activeKey} />
          : <TabStrip tabs={visibleTabs} onTabChange={onTabChange} activeKey={activeKey} />}
      </div>

      {/* Active tab content (route-driven via Outlet, or children for state-driven) */}
      <div className="px-2 lg:px-4">
        {children ?? <Outlet />}
      </div>
    </div>
  );
}

/**
 * Standalone tab strip — exported so a hub can render it inline
 * without using <TabHub> wrapper (e.g. for inline tab strips inside
 * an entity detail page).
 */
export function TabStrip({
  tabs, onTabChange, activeKey,
}: {
  tabs: TabDef[];
  onTabChange?: (key: string) => void;
  activeKey?: string;
}) {
  const loc = useLocation();
  const visibleTabs = tabs.filter((t) => t.show !== false);

  return (
    <nav className="px-2 lg:px-4">
      <div className="flex gap-2 overflow-x-auto scrollbar-thin pb-1 -mb-1
                      [scrollbar-width:thin]
                      [scrollbar-color:rgba(115,115,115,0.4)_transparent]">
        {visibleTabs.map((t) => {
          if (t.to) {
            // Route-driven
            const isActive = t.matchPrefix
              ? loc.pathname === t.to || loc.pathname.startsWith(t.matchPrefix + '/') || loc.pathname.startsWith(t.matchPrefix)
              : loc.pathname === t.to;
            return <PillLink key={t.to} tab={t} active={isActive} />;
          }
          // State-driven
          const isActive = t.key === activeKey;
          return (
            <PillButton
              key={t.key || t.label}
              tab={t}
              active={isActive}
              onClick={() => t.key && onTabChange?.(t.key)}
            />
          );
        })}
      </div>
    </nav>
  );
}

// Pill geometry shared by PillLink + PillButton. Surface tokens are
// the same arena/neon palette MagentaTabBar uses; the active fill is
// violet to match admin's brand identity (TopPillNav active pill).
const PILL_BASE = `shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-full
                   text-[12.5px] font-semibold border transition active:scale-95`;
const PILL_ACTIVE = `bg-violet-600 text-white border-violet-600
                     shadow-[0_2px_12px_rgba(139,92,246,0.45)]`;
const PILL_INACTIVE = `bg-white text-arena-700 border-arena-200/60 hover:bg-arena-50
                       dark:bg-arena-800/60 dark:text-neon-200/80 dark:border-arena-600/40
                       dark:hover:bg-arena-700/40`;

function PillLink({ tab, active }: { tab: TabDef; active: boolean }) {
  return (
    <NavLink
      to={tab.to!}
      end={tab.to === '/'}
      className={`${PILL_BASE} ${active ? PILL_ACTIVE : PILL_INACTIVE}`}
    >
      {tab.icon && <Icon name={tab.icon} size={14} />}
      <span>{tab.label}</span>
      {typeof tab.count === 'number' && tab.count > 0 && (
        <Badge value={tab.count} active={active} />
      )}
    </NavLink>
  );
}

function PillButton({ tab, active, onClick }: { tab: TabDef; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`${PILL_BASE} ${active ? PILL_ACTIVE : PILL_INACTIVE}`}
    >
      {tab.icon && <Icon name={tab.icon} size={14} />}
      <span>{tab.label}</span>
      {typeof tab.count === 'number' && tab.count > 0 && (
        <Badge value={tab.count} active={active} />
      )}
    </button>
  );
}

function Badge({ value, active }: { value: number; active: boolean }) {
  return (
    <span
      className={`px-1.5 py-0.5 text-[10px] font-bold rounded-full tabular-nums ${
        active
          ? 'bg-white/25 text-white'
          : 'bg-arena-50 text-arena-700 dark:bg-arena-700/50 dark:text-neon-200/80'
      }`}
    >
      {value > 99 ? '99+' : value}
    </span>
  );
}

/**
 * SegmentedTabs — Phase 16.8 + 16.9.
 * Quieter visual variant of TabStrip. Flat container with subtle
 * white-on-white active state (iOS / Material segmented-control feel).
 *
 * Phase 16.8 introduced state-driven mode for entity-detail pages
 * (player edit, agent edit, sub-agent edit).
 * Phase 16.9 added route-driven mode (TabDef.to + matchPrefix), so
 * the same primitive can also drive the platform's hub pages
 * (Finance, People, Games, etc.). One source of truth for tabs.
 *
 * Phase 1 UI overhaul (2026-05-15): surface tokens realigned with the
 * arena/neon palette MagentaTabBar uses so the segmented container
 * matches the agent SPA's hub bars verbatim. Active-tab fill on the
 * 'block' variant uses violet (admin brand) instead of fuchsia.
 *
 * Picks mode per-tab:
 *   - tab.to present  → renders a NavLink, active = current path
 *                       matches `to` (or matchPrefix if provided)
 *   - tab.key present → renders a button, active = key === activeKey,
 *                       click fires onTabChange(key)
 */
export function SegmentedTabs({
  tabs, onTabChange, activeKey, className, variant = 'segmented',
}: {
  tabs: TabDef[];
  /** State-driven only — required when any tab uses key+activeKey. */
  onTabChange?: (key: string) => void;
  /** State-driven only — required when any tab uses key+activeKey. */
  activeKey?: string;
  className?: string;
  /**
   * Phase R7 follow-up (2026-05-13). Two visual variants:
   *   - 'segmented' (default) — the existing quiet white-on-white
   *     iOS segmented-control look. Used by every admin/agent hub.
   *   - 'block' — the player-frontend Profile-page look: equal-
   *     width fully-coloured pills, brand-on-white active fill,
   *     visible neutral inactive blocks. In this variant icons +
   *     counts are SUPPRESSED to mirror the player Profile tab bar.
   */
  variant?: 'segmented' | 'block';
}) {
  const loc = useLocation();
  const visibleTabs = tabs.filter((t) => t.show !== false);

  // Visual class per variant + active state. Identical for buttons and
  // NavLinks so the two modes look the same.
  const innerClass = (active: boolean) => {
    if (variant === 'block') {
      // Block variant — admin's violet replaces the previous fuchsia
      // accent so this matches TopPillNav + the hub's active pill.
      return `flex-1 py-2 text-xs sm:text-sm font-semibold rounded-lg transition text-center
         ${active
           ? 'bg-violet-600 text-white shadow-[0_2px_10px_rgba(139,92,246,0.4)]'
           : 'bg-arena-50 dark:bg-arena-800/60 text-arena-700 dark:text-neon-200/70 hover:bg-arena-100 dark:hover:bg-arena-700/60'}`;
    }
    // Default 'segmented' variant — arena/neon tokens replace the
    // previous neutral-* hardcodes so this looks the same as the
    // agent SPA's MagentaTabBar 'sm' size pills.
    return `flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-lg transition
       ${active
         ? 'bg-white dark:bg-arena-700/60 text-arena-900 dark:text-white shadow-sm'
         : 'text-arena-600 dark:text-neon-200/60 hover:text-arena-900 dark:hover:text-white'}`;
  };

  const renderInner = (t: TabDef, active: boolean) => {
    // In 'block' variant, suppress icons + counts to match the
    // text-only player-frontend Profile tabs.
    if (variant === 'block') {
      return <span>{t.label}</span>;
    }
    return (
      <>
        {t.icon && <Icon name={t.icon} size={14} />}
        <span>{t.label}</span>
        {typeof t.count === 'number' && t.count > 0 && (
          <span
            className={`px-1.5 py-0 text-[10px] font-bold rounded-full tabular-nums ${
              active
                ? 'bg-arena-100 text-arena-700 dark:bg-white/15 dark:text-white'
                : 'bg-arena-100 text-arena-600 dark:bg-arena-700/50 dark:text-neon-200/70'
            }`}
          >
            {t.count > 99 ? '99+' : t.count}
          </span>
        )}
      </>
    );
  };

  // Outer container — arena-tinted glass strip in both variants. The
  // 'block' variant uses a slightly heavier outer frame + tighter gap
  // to read as a single tactile segmented control (matches the player
  // Profile tab bar). The 'segmented' container is the gentler iOS
  // segmented-control look used by every admin hub.
  const outerClass = variant === 'block'
    ? `flex gap-1 p-1 rounded-xl
       bg-white/70 dark:bg-arena-800/60
       border border-arena-200/60 dark:border-arena-600/40 ${className || ''}`
    : `flex flex-wrap gap-1 rounded-xl
       bg-white/70 dark:bg-arena-800/60
       border border-arena-200/60 dark:border-arena-600/40
       p-1 ${className || ''}`;

  return (
    <div className={outerClass}>
      {visibleTabs.map((t) => {
        if (t.to) {
          // Route-driven (hub pages)
          const active = t.matchPrefix
            ? loc.pathname === t.to || loc.pathname.startsWith(t.matchPrefix + '/') || loc.pathname.startsWith(t.matchPrefix)
            : loc.pathname === t.to;
          return (
            <NavLink key={t.to} to={t.to} end={t.to === '/'} className={innerClass(active)}>
              {renderInner(t, active)}
            </NavLink>
          );
        }
        // State-driven (entity detail pages)
        const active = t.key === activeKey;
        return (
          <button
            key={t.key || t.label}
            type="button"
            onClick={() => t.key && onTabChange?.(t.key)}
            className={innerClass(active)}
          >
            {renderInner(t, active)}
          </button>
        );
      })}
    </div>
  );
}
